/* ===================================================================
   AdminViewProvider -- Option A "operator views a client's dashboard"
   (Liskov + Corbato, 2026-06-22).

   When an operator opens /agency/tenant/:id, the REAL client Dashboard +
   Reviews components are mounted inside <AdminViewProvider clientId=...>.
   This provider sets the ONE context switch every data + action hook consults:

     - DATA hooks (useReviews, useDashboardSummary, useActivity,
       useNotifications, useRequestsSummary, useMe, useBusiness) read from the
       ADMIN-SCOPED, read-only endpoints (gated by @require_admin on the
       Supabase user JWT) for the named client_id, INSTEAD of the per-tenant
       rr_-token endpoints -- so the operator never touches their own token's
       tenant and never needs an impersonation token (that would be Option B).

     - ACTION hooks (useOutboxActions, useUpdateBusiness, useMarkNotifications)
       become INERT no-ops. This is defense-in-depth ONLY: the real read-only
       guarantee is server-side -- there is NO admin write endpoint to call,
       so even a hand-crafted request cannot mutate a client's tenant. The UI
       inertness just stops the components rendering broken actions.

   The context object + the useAdminView() hook live in ./adminViewContext.ts
   (a non-component module) so this file only exports a component.
   =================================================================== */
import { useMemo, type ReactNode } from 'react';
import { AdminViewContext, type AdminViewState } from './adminViewContext';

export function AdminViewProvider({
  clientId,
  children,
}: {
  clientId: string;
  children: ReactNode;
}) {
  const value = useMemo<AdminViewState>(
    () => ({ clientId, readOnly: true }),
    [clientId],
  );
  return <AdminViewContext.Provider value={value}>{children}</AdminViewContext.Provider>;
}
